/** @jsx jsx */
/* eslint-disable jsx-a11y/label-has-associated-control, max-len */
import React, { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/core';
import moment from 'moment';
import Modal from 'components/molecules/Modal';
import { BoardData, PrintSetting } from 'domain/attend/shiftBoard/types';
import {
 DEFAULT_PRINT_SETTING, PRINT_ITEMS, PRINT_SCALES, resolvePrintSetting,
} from 'domain/attend/shiftBoard/print';
import { ViewRange } from 'domain/attend/shiftBoard/time';
import { boardStyles, mock } from './styles';
import BoardDatePicker from './BoardDatePicker';
import BoardSelect from './BoardSelect';

const styles = {
  section: css({
    background: mock.gray10,
    color: mock.textMain,
    fontWeight: 700,
    fontSize: '12px',
    padding: '6px 10px',
    margin: '14px -4px 10px',
    '&:first-of-type': { marginTop: 0 },
  }),
  row: css({
    display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap',
  }),
  label: css({
    fontSize: '12px', color: mock.textMain, minWidth: '128px', flex: 'none',
  }),
  quick: css({
    background: '#fff',
    border: `1px solid ${mock.gray20}`,
    borderRadius: '4px',
    fontSize: '12px',
    padding: '5px 14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: mock.textMain,
  }),
  choiceGroup: css({
    display: 'flex', gap: '8px', flex: '1 1 auto', minWidth: 0,
  }),
  choice: css({
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: `1px solid ${mock.gray20}`,
    borderRadius: '4px',
    padding: '7px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    background: '#fff',
    whiteSpace: 'nowrap',
    '&[data-on="true"]': { borderColor: mock.primary, color: mock.primary, fontWeight: 700 },
  }),
  color: css({
    width: '38px', height: '30px', padding: '2px', border: `1px solid ${mock.gray20}`, borderRadius: '4px', background: '#fff', cursor: 'pointer',
  }),
  items: css({
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '6px 12px',
  }),
  itemBox: css({
    display: 'flex', alignItems: 'center', gap: '6px', border: `1px solid ${mock.gray20}`, borderRadius: '4px', padding: '6px 8px', fontSize: '12px', cursor: 'pointer',
  }),
  reset: css({
    background: 'transparent', border: 'none', color: mock.primary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', padding: 0,
  }),
};

export type PrintSettingsModalProps = {
  open: boolean;
  board: BoardData | null;
  /** シフト表が表示している期間。「表示中の期間」の初期値に使う */
  range: ViewRange;
  setting: Partial<PrintSetting> | null;
  onClose: () => void;
  /** 押した時点の設定は、いつも自分用に覚える（9/24。チェックはなくした） */
  onPrint: (setting: PrintSetting) => void;
};

/**
 * 印刷設定。期間・レイアウト・表示項目の 3 つに分けて並べる
 */
const PrintSettingsModal: React.FC<PrintSettingsModalProps> = ({
  open, board, range, setting, onClose, onPrint,
}) => {
  const [draft, setDraft] = useState<PrintSetting>(resolvePrintSetting(setting));

  useEffect(() => {
    if (open) {
      const resolved = resolvePrintSetting(setting);
      setDraft({
        ...resolved,
        dateFrom: resolved.dateFrom || range.from,
        dateTo: resolved.dateTo || range.to,
      });
    }
  }, [open, setting, range.from, range.to]);

  const set = (patch: Partial<PrintSetting>) => setDraft({ ...draft, ...patch });
  const toggleItem = (key: string, on: boolean) => {
    set({ items: on ? [...draft.items.filter((k) => k !== key), key] : draft.items.filter((k) => k !== key) });
  };
  const quick = (unit: 'day' | 'week' | 'month') => {
    const base = moment();
    const from = unit === 'day' ? base.clone() : base.clone().startOf(unit === 'week' ? 'isoWeek' : 'month');
    const to = unit === 'day' ? base.clone() : base.clone().endOf(unit === 'week' ? 'isoWeek' : 'month');
    set({ period: 'custom', dateFrom: from.format('YYYY-MM-DD'), dateTo: to.format('YYYY-MM-DD') });
  };

  return (
    <Modal open={open} closeHandler={onClose} title="印刷" submitText="印刷ビューを開く" onSubmit={() => onPrint(draft)} width="560px">
      <div css={boardStyles.modalBody}>
        <div css={styles.section}>期間・店舗・業務</div>

        <div css={styles.row}>
          <span css={styles.label}>印刷する期間</span>
          <button type="button" css={styles.quick} onClick={() => quick('day')}>今日</button>
          <button type="button" css={styles.quick} onClick={() => quick('week')}>今週</button>
          <button type="button" css={styles.quick} onClick={() => quick('month')}>今月</button>
          <button type="button" css={styles.quick} onClick={() => set({ period: 'current' })}>表示中</button>
        </div>
        <div css={styles.row}>
          <span css={styles.label} />
          {/* 月次帳票などと同じカレンダー（10/6） */}
          <BoardDatePicker
            id="pr-date-from"
            value={draft.period === 'current' ? range.from : (draft.dateFrom || range.from)}
            height={32}
            inModal={true}
            onChange={(v) => set({ period: 'custom', dateFrom: v })}
          />
          <span style={{ fontSize: '12px', color: mock.textSub }}>〜</span>
          <BoardDatePicker
            id="pr-date-to"
            value={draft.period === 'current' ? range.to : (draft.dateTo || range.to)}
            height={32}
            inModal={true}
            onChange={(v) => set({ period: 'custom', dateTo: v })}
          />
          {draft.period === 'current' && <span style={{ fontSize: '11px', color: mock.textSub }}>表示中の期間</span>}
        </div>
        <div css={styles.row}>
          <span css={styles.label}>店舗</span>
          <span style={{ fontSize: '12px' }}>{board ? board.org.orgName : ''}</span>
          <span style={{ fontSize: '11px', color: mock.textSub }}>シフト表で選んでいる店舗を印刷します</span>
        </div>
        <div css={styles.row}>
          <span css={styles.label}>業務</span>
          <span style={{ fontSize: '12px' }}>{board && board.businesses.length > 0 ? 'シフト表の絞り込みに従います' : '設定なし'}</span>
        </div>

        <div css={styles.section}>レイアウト設定</div>

        <div css={styles.row}>
          <span css={styles.label}>ページの向き</span>
          <div css={styles.choiceGroup}>
            <label css={styles.choice} data-on={draft.orientation === 'landscape'} htmlFor="pr-land">
              <input id="pr-land" type="radio" name="pr-orientation" checked={draft.orientation === 'landscape'} onChange={() => set({ orientation: 'landscape' })} />
              横向き
            </label>
            <label css={styles.choice} data-on={draft.orientation === 'portrait'} htmlFor="pr-port">
              <input id="pr-port" type="radio" name="pr-orientation" checked={draft.orientation === 'portrait'} onChange={() => set({ orientation: 'portrait' })} />
              縦向き
            </label>
          </div>
        </div>
        <div css={styles.row}>
          <span css={styles.label}>用紙</span>
          {/* ほかの画面と同じ共通の Select（10/7） */}
          <BoardSelect
            id="pr-paper"
            label="用紙"
            value={draft.paper}
            onChange={(v) => set({ paper: v as PrintSetting['paper'] })}
            options={[{ value: 'A4', label: 'A4' }, { value: 'A3', label: 'A3' }]}
            style={{ width: '100px' }}
          />
          <label htmlFor="pr-fit" style={{ fontSize: '12px' }}>
            <input id="pr-fit" type="checkbox" checked={draft.fitToWidth} onChange={(e) => set({ fitToWidth: e.target.checked })} />
            {' '}
            用紙幅に収める
          </label>
        </div>
        <div css={styles.row}>
          <label css={styles.choice} data-on={draft.splitByBusiness} htmlFor="pr-split" style={{ flex: '1 1 auto' }}>
            <input id="pr-split" type="checkbox" checked={draft.splitByBusiness} onChange={(e) => set({ splitByBusiness: e.target.checked })} />
            業務をページごとに分けて印刷
          </label>
        </div>
        <div css={styles.row}>
          <span css={styles.label}>枠線</span>
          <input css={styles.color} type="color" value={draft.borderColor} onChange={(e) => set({ borderColor: e.target.value })} title="枠線の色" />
          <span css={styles.label} style={{ minWidth: '96px' }}>背景色（偶数行）</span>
          <input css={styles.color} type="color" value={draft.stripeColor || '#F2F2F2'} onChange={(e) => set({ stripeColor: e.target.value })} title="偶数行の背景色" />
          <label htmlFor="pr-stripe-off" style={{ fontSize: '11px', color: mock.textSub }}>
            <input id="pr-stripe-off" type="checkbox" checked={draft.stripeColor === null} onChange={(e) => set({ stripeColor: e.target.checked ? null : '#F2F2F2' })} />
            {' '}
            塗らない
          </label>
        </div>
        <div css={styles.row}>
          <span css={styles.label}>余白</span>
          <label htmlFor="pr-mt" style={{ fontSize: '12px' }}>
            <input id="pr-mt" type="checkbox" checked={draft.marginTop} onChange={(e) => set({ marginTop: e.target.checked })} />
            {' '}
            上
          </label>
          <label htmlFor="pr-ml" style={{ fontSize: '12px' }}>
            <input id="pr-ml" type="checkbox" checked={draft.marginLeft} onChange={(e) => set({ marginLeft: e.target.checked })} />
            {' '}
            左
          </label>
          <label htmlFor="pr-mr" style={{ fontSize: '12px' }}>
            <input id="pr-mr" type="checkbox" checked={draft.marginRight} onChange={(e) => set({ marginRight: e.target.checked })} />
            {' '}
            右
          </label>
        </div>
        <div css={styles.row}>
          <span css={styles.label}>見出しの文字サイズ</span>
          <BoardSelect
            id="pr-header-scale"
            label="見出しの文字サイズ"
            value={String(draft.headerScale)}
            onChange={(v) => set({ headerScale: parseInt(v, 10) })}
            options={PRINT_SCALES.map((v) => ({ value: String(v), label: `${v}%` }))}
            style={{ width: '100px' }}
          />
        </div>
        <div css={styles.row}>
          <span css={styles.label}>スタッフ名の文字サイズ</span>
          <BoardSelect
            id="pr-staff-scale"
            label="スタッフ名の文字サイズ"
            value={String(draft.staffNameScale)}
            onChange={(v) => set({ staffNameScale: parseInt(v, 10) })}
            options={PRINT_SCALES.map((v) => ({ value: String(v), label: `${v}%` }))}
            style={{ width: '100px' }}
          />
        </div>
        <div css={styles.row}>
          <span css={styles.label}>本文の文字サイズ</span>
          <BoardSelect
            id="pr-font"
            label="本文の文字サイズ"
            value={draft.fontSize}
            onChange={(v) => set({ fontSize: v as PrintSetting['fontSize'] })}
            options={[
              { value: 'large', label: '大きめ（店頭掲示向け）' },
              { value: 'medium', label: '標準' },
              { value: 'small', label: '小さめ' },
            ]}
            style={{ width: '220px' }}
          />
          <label htmlFor="pr-mono" style={{ fontSize: '12px' }}>
            <input id="pr-mono" type="checkbox" checked={draft.monochrome} onChange={(e) => set({ monochrome: e.target.checked })} />
            {' '}
            モノクロ
          </label>
        </div>

        <div css={styles.section}>表示項目</div>

        <div css={styles.items}>
          <label css={styles.itemBox} htmlFor="pr-item-header">
            <input id="pr-item-header" type="checkbox" checked={draft.header} onChange={(e) => set({ header: e.target.checked })} />
            見出し（店名・期間）
          </label>
          {PRINT_ITEMS.map((it) => (
            <label key={it.key} css={styles.itemBox} htmlFor={`pr-item-${it.key}`}>
              <input id={`pr-item-${it.key}`} type="checkbox" checked={draft.items.indexOf(it.key) !== -1} onChange={(e) => toggleItem(it.key, e.target.checked)} />
              {it.label}
            </label>
          ))}
        </div>

        <div css={styles.row} style={{ marginTop: '14px', justifyContent: 'flex-end' }}>
          <button type="button" css={styles.reset} onClick={() => setDraft({ ...DEFAULT_PRINT_SETTING, dateFrom: range.from, dateTo: range.to })}>初期設定に戻す</button>
        </div>
      </div>
    </Modal>
  );
};

export default PrintSettingsModal;
